import { AxiosError } from 'axios';
import {
  type PostDocumentDuplicateRequestType,
  usePostDocumentDuplicate,
} from 'entities/document/api';
import { useShowNotification } from 'hooks/useShowNotification';
import { useTranslation } from 'react-i18next';

export const useDuplicateDocument = (documentId?: number) => {
  const { t } = useTranslation('documents');
  const showNotification = useShowNotification();
  const { mutateAsync: duplicateMutation } = usePostDocumentDuplicate();

  const duplicate = async (
    body: PostDocumentDuplicateRequestType['body'],
  ): Promise<{ id: number | string; name: string } | null> => {
    if (!documentId) {
      throw new Error('Document ID is required to duplicate a document.');
    }

    try {
      const result = await duplicateMutation({ documentId, body });

      showNotification({
        message: t('documents:notification.documentDuplicated'),
      });

      if (result.response?.type === 'CREATED_DOCUMENTS') {
        return result.response.documents[0];
      }

      throw new Error('Failed to duplicate document');
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 409) {
        showNotification({
          message: t('documents:notification.templateOutdated'),
          variant: 'error',
        });
        return null;
      }
      throw error;
    }
  };

  return { duplicate };
};
